import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { activateCalculatorBootstrapUser, clearCalculatorBootstrapCache } from './calculatorBootstrapCache'
import { DOUYIN_SHUIJU_STORE_ID, DOUYIN_YOUHE_STORE_ID, DOUYIN_STORE_IDS, douyinPath } from './douyinStore'

export type PermissionAction = 'view' | 'save' | 'edit' | 'upload'

export interface UserPermission {
  storeId: string
  pageKey: string
  action: PermissionAction
}

export interface AuthUser {
  id: string
  phone: string
  username: string
  isAdmin: boolean
  enabled: boolean
  mustChangePassword: boolean
  permissions: UserPermission[]
}

interface AuthPayload {
  authenticated: boolean
  setupRequired: boolean
  user: AuthUser
  csrfToken: string
  expiresAt: number
}

interface AuthContextValue {
  loading: boolean
  user: AuthUser | null
  setupRequired: boolean
  login: (phone: string, password: string) => Promise<AuthUser>
  logout: () => Promise<void>
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>
  canAccess: (storeId: string, pageKey: string, action?: PermissionAction) => boolean
  refresh: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)
let currentCsrfToken = ''

export async function authFetch(input: RequestInfo | URL, init: RequestInit = {}) {
  const method = (init.method || 'GET').toUpperCase()
  const headers = new Headers(init.headers)
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method) && currentCsrfToken) {
    headers.set('X-CSRF-Token', currentCsrfToken)
  }
  return fetch(input, { ...init, headers })
}

async function jsonPayload<T>(response: Response): Promise<T & { error?: string; code?: string }> {
  const payload = await response.json().catch(() => ({}))
  return payload as T & { error?: string; code?: string }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState<AuthUser | null>(null)
  const [setupRequired, setSetupRequired] = useState(false)

  const applyPayload = useCallback((payload: AuthPayload) => {
    currentCsrfToken = payload.csrfToken
    activateCalculatorBootstrapUser(payload.user.id)
    setUser(payload.user)
    setSetupRequired(Boolean(payload.setupRequired))
  }, [])

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const response = await fetch('/api/auth/me', { cache: 'no-store' })
      const payload = await jsonPayload<AuthPayload & { setupRequired?: boolean }>(response)
      if (!response.ok) {
        currentCsrfToken = ''
        clearCalculatorBootstrapCache()
        setUser(null)
        setSetupRequired(Boolean(payload.setupRequired))
        return
      }
      applyPayload(payload)
    } finally {
      setLoading(false)
    }
  }, [applyPayload])

  useEffect(() => { void refresh() }, [refresh])

  const login = useCallback(async (phone: string, password: string) => {
    const response = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone, password }),
    })
    const payload = await jsonPayload<AuthPayload>(response)
    if (!response.ok) throw new Error(payload.error || '登录失败')
    applyPayload(payload)
    return payload.user
  }, [applyPayload])

  const logout = useCallback(async () => {
    await authFetch('/api/auth/logout', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' })
    currentCsrfToken = ''
    clearCalculatorBootstrapCache()
    setUser(null)
  }, [])

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    const response = await authFetch('/api/auth/change-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ currentPassword, newPassword }),
    })
    const payload = await jsonPayload<{ reauthenticate?: boolean }>(response)
    if (!response.ok) throw new Error(payload.error || '密码修改失败')
    currentCsrfToken = ''
    clearCalculatorBootstrapCache()
    setUser(null)
  }, [])

  const canAccess = useCallback((storeId: string, pageKey: string, action: PermissionAction = 'view') => {
    if (!user?.enabled) return false
    if (user.isAdmin) return true
    return user.permissions.some((permission) =>
      permission.storeId === storeId && permission.pageKey === pageKey && permission.action === action,
    )
  }, [user])

  const value = useMemo<AuthContextValue>(() => ({
    loading, user, setupRequired, login, logout, changePassword, canAccess, refresh,
  }), [canAccess, changePassword, loading, login, logout, refresh, setupRequired, user])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const value = useContext(AuthContext)
  if (!value) throw new Error('useAuth must be used within AuthProvider')
  return value
}

const PAGE_ROUTES: Array<[string, string, string]> = [
  ['/company/products', 'company', 'company.products'], ['/company/mappings', 'company', 'company.mappings'], ['/company', 'company', 'company.overview'],
  ['/pinduoduo/profit-calculator', 'pdd-beijuxiong-youhe', 'pdd.profit_calculator'], ['/pinduoduo/data-upload', 'pdd-beijuxiong-youhe', 'pdd.data_upload'],
  ['/pinduoduo/after-sales', 'pdd-beijuxiong-youhe', 'pdd.after_sales'], ['/pinduoduo/product-master', 'pdd-beijuxiong-youhe', 'pdd.product_master'],
  ['/pinduoduo/costs', 'pdd-beijuxiong-youhe', 'pdd.costs'], ['/pinduoduo/products', 'pdd-beijuxiong-youhe', 'pdd.products'],
  ['/pinduoduo/product/', 'pdd-beijuxiong-youhe', 'pdd.products'], ['/pinduoduo/spu', 'pdd-beijuxiong-youhe', 'pdd.spu'],
  ['/pinduoduo/sku', 'pdd-beijuxiong-youhe', 'pdd.sku'], ['/pinduoduo', 'pdd-beijuxiong-youhe', 'pdd.overview'],
  ['/douyin/spu', 'dy-beddypop-shuiju', 'dy.spu'], ['/douyin/sku', 'dy-beddypop-shuiju', 'dy.sku'],
  ['/douyin/live', 'dy-beddypop-shuiju', 'dy.live'], ['/douyin/costs', 'dy-beddypop-shuiju', 'dy.costs'],
  ['/douyin', 'dy-beddypop-shuiju', 'dy.spu'],
  ['/jd/profit-calculator', 'jd-588485', 'jd.profit_calculator'], ['/jd/data-update', 'jd-588485', 'jd.data'], ['/jd/costs', 'jd-588485', 'jd.costs'],
  ['/jd/orders', 'jd-588485', 'jd.orders'],
  ['/jd/warehouse-slow-moving', 'jd-588485', 'jd.inventory'],
  ['/realtime', 'jd-588485', 'jd.realtime'], ['/profit', 'jd-588485', 'jd.profit'], ['/analysis', 'jd-588485', 'jd.analysis'],
  ['/advertising/fee-ratio', 'jd-588485', 'jd.spend_comparison'], ['/advertising/spend-comparison', 'jd-588485', 'jd.spend_comparison'],
  ['/advertising/orders', 'jd-588485', 'jd.advertising'], ['/advertising', 'jd-588485', 'jd.advertising'],
  ['/campaign-analysis', 'jd-588485', 'jd.campaigns'],
  ['/product-master', 'jd-588485', 'jd.product_master'], ['/new-products', 'jd-588485', 'jd.new_products'],
  ['/inventory', 'jd-588485', 'jd.inventory'], ['/segments', 'jd-588485', 'jd.campaigns'], ['/data', 'jd-588485', 'jd.data'],
  ['/spu', 'jd-588485', 'jd.spu'], ['/sku', 'jd-588485', 'jd.sku'], ['/product/spu/', 'jd-588485', 'jd.spu'], ['/product/sku/', 'jd-588485', 'jd.sku'],
]

const canView = (user: AuthUser, storeId: string, pageKey: string) =>
  user.permissions.some((permission) => permission.storeId === storeId && permission.pageKey === pageKey && permission.action === 'view')

export function defaultRouteFor(user: AuthUser): string {
  if (user.isAdmin) return '/company'
  const preferred: Array<[string, string, string]> = [
    ['jd-588485', 'jd.profit_calculator', '/jd/profit-calculator'],
    ['jd-588485', 'jd.spend_comparison', '/advertising/fee-ratio'],
    ['jd-588485', 'jd.data', '/jd/data-update'],
    ['pdd-beijuxiong-youhe', 'pdd.profit_calculator', '/pinduoduo/profit-calculator'],
    [DOUYIN_SHUIJU_STORE_ID, 'dy.spu', douyinPath(DOUYIN_SHUIJU_STORE_ID, '/spu')],
    [DOUYIN_YOUHE_STORE_ID, 'dy.spu', douyinPath(DOUYIN_YOUHE_STORE_ID, '/spu')],
  ]
  for (const [storeId, pageKey, route] of preferred) {
    if (canView(user, storeId, pageKey)) return route
  }
  if (canView(user, 'jd-588485', 'jd.overview')) return '/'
  for (const [path, storeId, pageKey] of PAGE_ROUTES) {
    if (path.endsWith('/')) continue
    if (canView(user, storeId, pageKey)) return path
  }
  return '/forbidden'
}

export function routePermission(pathname: string): [string, string] | null {
  if (pathname === '/') return ['jd-588485', 'jd.overview']
  const douyin = pathname.match(/^\/douyin\/([^/]+)\/(spu|sku|live|costs)(?:\/|$)/)
  if (douyin && DOUYIN_STORE_IDS.has(douyin[1])) {
    return [douyin[1], `dy.${douyin[2]}`]
  }
  const route = PAGE_ROUTES.find(([prefix]) => pathname === prefix || pathname.startsWith(prefix))
  return route ? [route[1], route[2]] : null
}
